import { useCallback, useState } from 'react'
import { Plus, Settings2 } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

import { MasterFormModal } from '@/components/masters/MasterFormModal'
import { MasterServerTable } from '@/components/masters/MasterServerTable'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  createDiscountReason,
  DISCOUNT_PERMISSION_OPTIONS,
  fetchDiscountReasonsList,
  fetchDiscountReasonsPage,
  updateDiscountReason,
} from '@/lib/api/discountReasons'
import {
  createPaymentMethod,
  fetchPaymentMethodsList,
  fetchPaymentMethodsPage,
  updatePaymentMethod,
} from '@/lib/api/paymentMethods'
import {
  createPromoCode,
  fetchPromoCodesList,
  fetchPromoCodesPage,
  PROMO_CODE_TYPE_OPTIONS,
  updatePromoCode,
} from '@/lib/api/promoCodes'
import { toAmount } from '@/lib/api/products'
import {
  createSupplier,
  fetchSuppliersList,
  fetchSuppliersPage,
  updateSupplier,
} from '@/lib/api/suppliers'
import {
  createTaxProfile,
  fetchTaxProfilesList,
  fetchTaxProfilesPage,
  updateTaxProfile,
} from '@/lib/api/taxProfiles'
import { PHONE_LOCAL_PLACEHOLDER } from '@/lib/phone'
import { cn } from '@/lib/utils'

const TABS = [
  { id: 'tax', label: 'Tax profiles' },
  { id: 'suppliers', label: 'Suppliers' },
  { id: 'promo', label: 'Promo codes' },
  { id: 'payments', label: 'Payment methods' },
  { id: 'discounts', label: 'Discount reasons' },
]

const inputClass = cn(
  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

function emptyTaxForm() {
  return { name: '', rate: 5, inclusive: true, country_code: 'AE', is_active: true }
}

function taxToForm(item) {
  return {
    name: item.name ?? '',
    rate: toAmount(item.rate),
    inclusive: Boolean(item.inclusive),
    country_code: item.country_code ?? 'AE',
    is_active: Boolean(item.is_active),
  }
}

function emptySupplierForm() {
  return {
    name: '',
    contact_name: '',
    phone: '',
    email: '',
    trn: '',
    payment_terms: '',
    is_active: true,
  }
}

function supplierToForm(item) {
  return {
    name: item.name ?? '',
    contact_name: item.contact_name ?? '',
    phone: item.phone ?? '',
    email: item.email ?? '',
    trn: item.trn ?? '',
    payment_terms: item.payment_terms ?? '',
    is_active: Boolean(item.is_active),
  }
}

function emptyPromoForm() {
  return {
    code: '',
    type: 'percent',
    value: '',
    date_from: '',
    date_to: '',
    max_uses: '',
    is_active: true,
  }
}

function promoToForm(item) {
  return {
    code: item.code ?? '',
    type: item.type ?? 'percent',
    value: toAmount(item.value),
    date_from: item.date_from ?? '',
    date_to: item.date_to ?? '',
    max_uses: item.max_uses ?? '',
    is_active: Boolean(item.is_active),
  }
}

function emptyPaymentForm() {
  return { code: '', name: '', sort: 0, is_active: true }
}

function paymentToForm(item) {
  return {
    code: item.code ?? '',
    name: item.name ?? '',
    sort: item.sort ?? 0,
    is_active: Boolean(item.is_active),
  }
}

function emptyDiscountForm() {
  return {
    code: '',
    name: '',
    required_permission: DISCOUNT_PERMISSION_OPTIONS[0]?.value ?? 'pos.discount',
    max_percent: '',
    sort: 0,
    is_active: true,
  }
}

function discountToForm(item) {
  return {
    code: item.code ?? '',
    name: item.name ?? '',
    required_permission: item.required_permission ?? 'pos.discount',
    max_percent: item.max_percent === null || item.max_percent === undefined ? '' : toAmount(item.max_percent),
    sort: item.sort ?? 0,
    is_active: Boolean(item.is_active),
  }
}

const TAX_EXPORT_COLUMNS = [
  { key: 'name', label: 'Name', getValue: (row) => row.name },
  { key: 'rate', label: 'Rate (%)', getValue: (row) => toAmount(row.rate) },
  { key: 'country_code', label: 'Country', getValue: (row) => row.country_code },
  { key: 'inclusive', label: 'Inclusive', getValue: (row) => (row.inclusive ? 'yes' : 'no') },
  { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
]

const SUPPLIER_EXPORT_COLUMNS = [
  { key: 'name', label: 'Name', getValue: (row) => row.name },
  { key: 'contact_name', label: 'Contact', getValue: (row) => row.contact_name ?? '' },
  { key: 'phone', label: 'Phone', getValue: (row) => row.phone ?? '' },
  { key: 'email', label: 'Email', getValue: (row) => row.email ?? '' },
  { key: 'trn', label: 'TRN', getValue: (row) => row.trn ?? '' },
  { key: 'payment_terms', label: 'Payment terms', getValue: (row) => row.payment_terms ?? '' },
  { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
]

const PROMO_EXPORT_COLUMNS = [
  { key: 'code', label: 'Code', getValue: (row) => row.code },
  { key: 'type', label: 'Type', getValue: (row) => row.type },
  { key: 'value', label: 'Value', getValue: (row) => toAmount(row.value) },
  { key: 'date_from', label: 'Valid from', getValue: (row) => row.date_from ?? '' },
  { key: 'date_to', label: 'Valid to', getValue: (row) => row.date_to ?? '' },
  { key: 'max_uses', label: 'Max uses', getValue: (row) => row.max_uses ?? '' },
  { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
]

const PAYMENT_EXPORT_COLUMNS = [
  { key: 'code', label: 'Code', getValue: (row) => row.code },
  { key: 'name', label: 'Name', getValue: (row) => row.name },
  { key: 'sort', label: 'Sort', getValue: (row) => row.sort },
  { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
]

const DISCOUNT_EXPORT_COLUMNS = [
  { key: 'code', label: 'Code', getValue: (row) => row.code },
  { key: 'name', label: 'Name', getValue: (row) => row.name },
  { key: 'required_permission', label: 'Permission', getValue: (row) => row.required_permission },
  {
    key: 'max_percent',
    label: 'Max discount (%)',
    getValue: (row) =>
      row.max_percent === null || row.max_percent === undefined ? '' : toAmount(row.max_percent),
  },
  { key: 'sort', label: 'Sort', getValue: (row) => row.sort },
  { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
]

export function CatalogMastersPage() {
  const { activeBranchId } = useBranch()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') ?? 'tax'

  const [modalOpen, setModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [reloadKey, setReloadKey] = useState(0)
  const [taxForm, setTaxForm] = useState(emptyTaxForm())
  const [supplierForm, setSupplierForm] = useState(emptySupplierForm())
  const [promoForm, setPromoForm] = useState(emptyPromoForm())
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm())
  const [discountForm, setDiscountForm] = useState(emptyDiscountForm())

  const fetchTaxPage = useCallback((params) => fetchTaxProfilesPage(params), [])
  const fetchSuppliersPageCb = useCallback((params) => fetchSuppliersPage(params), [])
  const fetchPromoPage = useCallback((params) => fetchPromoCodesPage(params), [])
  const fetchPaymentsPage = useCallback((params) => fetchPaymentMethodsPage(params), [])
  const fetchDiscountsPage = useCallback((params) => fetchDiscountReasonsPage(params), [])

  function switchTab(tabId) {
    setSearchParams({ tab: tabId })
    setModalOpen(false)
    setError('')
    setSuccess('')
  }

  function openCreate() {
    setCreating(true)
    setSelectedId(null)
    setError('')
    setSuccess('')
    if (activeTab === 'tax') setTaxForm(emptyTaxForm())
    if (activeTab === 'suppliers') setSupplierForm(emptySupplierForm())
    if (activeTab === 'promo') setPromoForm(emptyPromoForm())
    if (activeTab === 'payments') setPaymentForm(emptyPaymentForm())
    if (activeTab === 'discounts') setDiscountForm(emptyDiscountForm())
    setModalOpen(true)
  }

  function openEdit(item) {
    setCreating(false)
    setSelectedId(item.id)
    setError('')
    setSuccess('')
    if (activeTab === 'tax') setTaxForm(taxToForm(item))
    if (activeTab === 'suppliers') setSupplierForm(supplierToForm(item))
    if (activeTab === 'promo') setPromoForm(promoToForm(item))
    if (activeTab === 'payments') setPaymentForm(paymentToForm(item))
    if (activeTab === 'discounts') setDiscountForm(discountToForm(item))
    setModalOpen(true)
  }

  function closeModal() {
    if (submitting) return
    setModalOpen(false)
    setError('')
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      if (activeTab === 'tax') {
        const payload = { ...taxForm, rate: toAmount(taxForm.rate) }
        if (creating) {
          await createTaxProfile(payload)
          setSuccess('Tax profile created.')
        } else if (selectedId) {
          await updateTaxProfile(selectedId, payload)
          setSuccess('Tax profile saved.')
        }
      } else if (activeTab === 'suppliers') {
        const payload = {
          ...supplierForm,
          contact_name: supplierForm.contact_name || null,
          phone: supplierForm.phone || null,
          email: supplierForm.email || null,
          trn: supplierForm.trn || null,
          payment_terms: supplierForm.payment_terms || null,
        }
        if (creating) {
          await createSupplier(payload)
          setSuccess('Supplier created.')
        } else if (selectedId) {
          await updateSupplier(selectedId, payload)
          setSuccess('Supplier saved.')
        }
      } else if (activeTab === 'promo') {
        const payload = {
          ...promoForm,
          code: promoForm.code.toUpperCase(),
          value: toAmount(promoForm.value),
          date_from: promoForm.date_from || null,
          date_to: promoForm.date_to || null,
          max_uses: promoForm.max_uses ? Number(promoForm.max_uses) : null,
        }
        if (creating) {
          await createPromoCode(payload)
          setSuccess('Promo code created.')
        } else if (selectedId) {
          await updatePromoCode(selectedId, payload)
          setSuccess('Promo code saved.')
        }
      } else if (activeTab === 'payments') {
        const payload = {
          ...paymentForm,
          code: paymentForm.code.toLowerCase(),
          sort: Number(paymentForm.sort) || 0,
        }
        if (creating) {
          await createPaymentMethod(payload)
          setSuccess('Payment method created.')
        } else if (selectedId) {
          await updatePaymentMethod(selectedId, payload)
          setSuccess('Payment method saved.')
        }
      } else {
        const payload = {
          ...discountForm,
          code: discountForm.code.toLowerCase(),
          max_percent: discountForm.max_percent === '' ? null : toAmount(discountForm.max_percent),
          sort: Number(discountForm.sort) || 0,
        }
        if (creating) {
          await createDiscountReason(payload)
          setSuccess('Discount reason created.')
        } else if (selectedId) {
          await updateDiscountReason(selectedId, payload)
          setSuccess('Discount reason saved.')
        }
      }
      setReloadKey((current) => current + 1)
      setModalOpen(false)
    } catch (err) {
      setError(
        getFieldError(err, 'name') ??
          getFieldError(err, 'code') ??
          getFieldError(err, 'rate') ??
          err?.response?.data?.message ??
          'Unable to save.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const activeTabLabel = TABS.find((tab) => tab.id === activeTab)?.label ?? 'Entry'
  const modalTitle = creating ? `New ${activeTabLabel.toLowerCase()}` : `Edit ${activeTabLabel.toLowerCase()}`

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Settings2 className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Catalog settings</h1>
            <p className="text-sm text-muted-foreground">
              Tax profiles, suppliers, promo codes, payment methods, and discount reasons.
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          New entry
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => switchTab(tab.id)}
            className={cn(
              'rounded-full px-4 py-2 text-sm font-medium transition-colors',
              activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'bg-muted/70 hover:bg-muted',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'tax' ? (
        <MasterServerTable
          key={`${activeBranchId}-tax`}
          fetchPage={fetchTaxPage}
          reloadKey={reloadKey}
          getRowKey={(row) => row.id}
          onEdit={openEdit}
          emptyLabel="No tax profiles yet."
          searchPlaceholder="Search tax profiles…"
          exportColumns={TAX_EXPORT_COLUMNS}
          exportFilename="tax-profiles.csv"
          fetchAllForExport={(params) => fetchTaxProfilesList(params)}
          columns={[
            { key: 'name', label: 'Name', sortable: true, render: (row) => row.name },
            {
              key: 'rate',
              label: 'Rate',
              sortable: true,
              render: (row) => `${toAmount(row.rate)}%`,
            },
            { key: 'country_code', label: 'Country', render: (row) => row.country_code },
            {
              key: 'inclusive',
              label: 'Inclusive',
              render: (row) => (row.inclusive ? 'Yes' : 'No'),
            },
            {
              key: 'is_active',
              label: 'Status',
              render: (row) => (row.is_active ? 'Active' : 'Inactive'),
            },
          ]}
        />
      ) : null}

      {activeTab === 'suppliers' ? (
        <MasterServerTable
          key={`${activeBranchId}-suppliers`}
          fetchPage={fetchSuppliersPageCb}
          reloadKey={reloadKey}
          getRowKey={(row) => row.id}
          onEdit={openEdit}
          emptyLabel="No suppliers yet."
          searchPlaceholder="Search suppliers…"
          exportColumns={SUPPLIER_EXPORT_COLUMNS}
          exportFilename="suppliers.csv"
          fetchAllForExport={(params) => fetchSuppliersList(params)}
          columns={[
            { key: 'name', label: 'Name', sortable: true, render: (row) => row.name },
            {
              key: 'contact_name',
              label: 'Contact',
              render: (row) => row.contact_name || '—',
            },
            { key: 'phone', label: 'Phone', render: (row) => row.phone || '—' },
            { key: 'trn', label: 'TRN', render: (row) => row.trn || '—' },
            {
              key: 'is_active',
              label: 'Status',
              render: (row) => (row.is_active ? 'Active' : 'Inactive'),
            },
          ]}
        />
      ) : null}

      {activeTab === 'promo' ? (
        <MasterServerTable
          key={`${activeBranchId}-promo`}
          fetchPage={fetchPromoPage}
          reloadKey={reloadKey}
          getRowKey={(row) => row.id}
          onEdit={openEdit}
          emptyLabel="No promo codes yet."
          searchPlaceholder="Search promo codes…"
          exportColumns={PROMO_EXPORT_COLUMNS}
          exportFilename="promo-codes.csv"
          fetchAllForExport={(params) => fetchPromoCodesList(params)}
          columns={[
            { key: 'code', label: 'Code', sortable: true, render: (row) => row.code },
            { key: 'type', label: 'Type', render: (row) => row.type },
            {
              key: 'value',
              label: 'Value',
              sortable: true,
              render: (row) =>
                row.type === 'percent' ? `${toAmount(row.value)}%` : `${toAmount(row.value)} flat`,
            },
            {
              key: 'date_from',
              label: 'Valid from',
              render: (row) => row.date_from || '—',
            },
            {
              key: 'date_to',
              label: 'Valid to',
              render: (row) => row.date_to || '—',
            },
            {
              key: 'is_active',
              label: 'Status',
              render: (row) => (row.is_active ? 'Active' : 'Inactive'),
            },
          ]}
        />
      ) : null}

      {activeTab === 'payments' ? (
        <MasterServerTable
          key={`${activeBranchId}-payments`}
          fetchPage={fetchPaymentsPage}
          reloadKey={reloadKey}
          getRowKey={(row) => row.id}
          onEdit={openEdit}
          emptyLabel="No payment methods yet."
          searchPlaceholder="Search payment methods…"
          exportColumns={PAYMENT_EXPORT_COLUMNS}
          exportFilename="payment-methods.csv"
          fetchAllForExport={(params) => fetchPaymentMethodsList(params)}
          columns={[
            { key: 'code', label: 'Code', sortable: true, render: (row) => row.code },
            { key: 'name', label: 'Name', sortable: true, render: (row) => row.name },
            { key: 'sort', label: 'Sort', render: (row) => row.sort ?? 0 },
            {
              key: 'is_active',
              label: 'Status',
              render: (row) => (row.is_active ? 'Active' : 'Inactive'),
            },
          ]}
        />
      ) : null}

      {activeTab === 'discounts' ? (
        <MasterServerTable
          key={`${activeBranchId}-discounts`}
          fetchPage={fetchDiscountsPage}
          reloadKey={reloadKey}
          getRowKey={(row) => row.id}
          onEdit={openEdit}
          emptyLabel="No discount reasons yet."
          searchPlaceholder="Search discount reasons…"
          exportColumns={DISCOUNT_EXPORT_COLUMNS}
          exportFilename="discount-reasons.csv"
          fetchAllForExport={(params) => fetchDiscountReasonsList(params)}
          columns={[
            { key: 'code', label: 'Code', sortable: true, render: (row) => row.code },
            { key: 'name', label: 'Name', sortable: true, render: (row) => row.name },
            {
              key: 'required_permission',
              label: 'Permission',
              render: (row) => row.required_permission,
            },
            {
              key: 'max_percent',
              label: 'Max discount',
              render: (row) =>
                row.max_percent === null || row.max_percent === undefined
                  ? 'No cap'
                  : `${toAmount(row.max_percent)}%`,
            },
            {
              key: 'is_active',
              label: 'Status',
              render: (row) => (row.is_active ? 'Active' : 'Inactive'),
            },
          ]}
        />
      ) : null}

      <MasterFormModal
        open={modalOpen}
        onClose={closeModal}
        title={modalTitle}
        description={`Manage ${activeTabLabel.toLowerCase()} for catalog and POS settings.`}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {activeTab === 'tax' ? (
            <>
              <div>
                <label htmlFor="tax-name" className="mb-1.5 block text-sm font-medium">
                  Name
                </label>
                <input
                  id="tax-name"
                  className={inputClass}
                  value={taxForm.name}
                  onChange={(e) => setTaxForm((c) => ({ ...c, name: e.target.value }))}
                  required
                />
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label htmlFor="tax-rate" className="mb-1.5 block text-sm font-medium">
                    Rate (%)
                  </label>
                  <input
                    id="tax-rate"
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    className={inputClass}
                    value={taxForm.rate}
                    onChange={(e) => setTaxForm((c) => ({ ...c, rate: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="tax-country" className="mb-1.5 block text-sm font-medium">
                    Country
                  </label>
                  <input
                    id="tax-country"
                    maxLength={2}
                    className={cn(inputClass, 'uppercase')}
                    value={taxForm.country_code}
                    onChange={(e) =>
                      setTaxForm((c) => ({ ...c, country_code: e.target.value.toUpperCase() }))
                    }
                  />
                </div>
                <label className="flex items-end gap-2 pb-2 text-sm">
                  <input
                    type="checkbox"
                    checked={taxForm.inclusive}
                    onChange={(e) => setTaxForm((c) => ({ ...c, inclusive: e.target.checked }))}
                  />
                  Tax inclusive
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={taxForm.is_active}
                  onChange={(e) => setTaxForm((c) => ({ ...c, is_active: e.target.checked }))}
                />
                Profile is active
              </label>
            </>
          ) : null}

          {activeTab === 'suppliers' ? (
            <>
              <div>
                <label htmlFor="supplier-name" className="mb-1.5 block text-sm font-medium">
                  Name
                </label>
                <input
                  id="supplier-name"
                  className={inputClass}
                  value={supplierForm.name}
                  onChange={(e) => setSupplierForm((c) => ({ ...c, name: e.target.value }))}
                  required
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="supplier-contact" className="mb-1.5 block text-sm font-medium">
                    Contact name
                  </label>
                  <input
                    id="supplier-contact"
                    className={inputClass}
                    value={supplierForm.contact_name}
                    onChange={(e) => setSupplierForm((c) => ({ ...c, contact_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="supplier-phone" className="mb-1.5 block text-sm font-medium">
                    Phone
                  </label>
                  <input
                    id="supplier-phone"
                    className={inputClass}
                    value={supplierForm.phone}
                    onChange={(e) => setSupplierForm((c) => ({ ...c, phone: e.target.value }))}
                    placeholder={PHONE_LOCAL_PLACEHOLDER}
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="supplier-email" className="mb-1.5 block text-sm font-medium">
                    Email
                  </label>
                  <input
                    id="supplier-email"
                    type="email"
                    className={inputClass}
                    value={supplierForm.email}
                    onChange={(e) => setSupplierForm((c) => ({ ...c, email: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="supplier-trn" className="mb-1.5 block text-sm font-medium">
                    TRN
                  </label>
                  <input
                    id="supplier-trn"
                    className={inputClass}
                    value={supplierForm.trn}
                    onChange={(e) => setSupplierForm((c) => ({ ...c, trn: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="supplier-terms" className="mb-1.5 block text-sm font-medium">
                  Payment terms
                </label>
                <input
                  id="supplier-terms"
                  className={inputClass}
                  value={supplierForm.payment_terms}
                  onChange={(e) => setSupplierForm((c) => ({ ...c, payment_terms: e.target.value }))}
                  placeholder="Net 30"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={supplierForm.is_active}
                  onChange={(e) => setSupplierForm((c) => ({ ...c, is_active: e.target.checked }))}
                />
                Supplier is active
              </label>
            </>
          ) : null}

          {activeTab === 'promo' ? (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="promo-code" className="mb-1.5 block text-sm font-medium">
                    Code
                  </label>
                  <input
                    id="promo-code"
                    className={cn(inputClass, 'uppercase')}
                    value={promoForm.code}
                    onChange={(e) => setPromoForm((c) => ({ ...c, code: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="promo-type" className="mb-1.5 block text-sm font-medium">
                    Type
                  </label>
                  <select
                    id="promo-type"
                    className={inputClass}
                    value={promoForm.type}
                    onChange={(e) => setPromoForm((c) => ({ ...c, type: e.target.value }))}
                  >
                    {PROMO_CODE_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label htmlFor="promo-value" className="mb-1.5 block text-sm font-medium">
                    Value
                  </label>
                  <input
                    id="promo-value"
                    type="number"
                    min={0}
                    step="0.01"
                    className={inputClass}
                    value={promoForm.value}
                    onChange={(e) => setPromoForm((c) => ({ ...c, value: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="promo-from" className="mb-1.5 block text-sm font-medium">
                    Valid from
                  </label>
                  <input
                    id="promo-from"
                    type="date"
                    className={inputClass}
                    value={promoForm.date_from}
                    onChange={(e) => setPromoForm((c) => ({ ...c, date_from: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="promo-to" className="mb-1.5 block text-sm font-medium">
                    Valid to
                  </label>
                  <input
                    id="promo-to"
                    type="date"
                    className={inputClass}
                    value={promoForm.date_to}
                    onChange={(e) => setPromoForm((c) => ({ ...c, date_to: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="promo-max" className="mb-1.5 block text-sm font-medium">
                  Max uses
                </label>
                <input
                  id="promo-max"
                  type="number"
                  min={1}
                  className={inputClass}
                  value={promoForm.max_uses}
                  onChange={(e) => setPromoForm((c) => ({ ...c, max_uses: e.target.value }))}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={promoForm.is_active}
                  onChange={(e) => setPromoForm((c) => ({ ...c, is_active: e.target.checked }))}
                />
                Promo code is active
              </label>
            </>
          ) : null}

          {activeTab === 'payments' ? (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="payment-code" className="mb-1.5 block text-sm font-medium">
                    Code
                  </label>
                  <input
                    id="payment-code"
                    className={cn(inputClass, 'lowercase')}
                    value={paymentForm.code}
                    onChange={(e) => setPaymentForm((c) => ({ ...c, code: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="payment-name" className="mb-1.5 block text-sm font-medium">
                    Name
                  </label>
                  <input
                    id="payment-name"
                    className={inputClass}
                    value={paymentForm.name}
                    onChange={(e) => setPaymentForm((c) => ({ ...c, name: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="payment-sort" className="mb-1.5 block text-sm font-medium">
                  Sort order
                </label>
                <input
                  id="payment-sort"
                  type="number"
                  min={0}
                  className={inputClass}
                  value={paymentForm.sort}
                  onChange={(e) => setPaymentForm((c) => ({ ...c, sort: e.target.value }))}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={paymentForm.is_active}
                  onChange={(e) => setPaymentForm((c) => ({ ...c, is_active: e.target.checked }))}
                />
                Payment method is active
              </label>
            </>
          ) : null}

          {activeTab === 'discounts' ? (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="discount-code" className="mb-1.5 block text-sm font-medium">
                    Code
                  </label>
                  <input
                    id="discount-code"
                    className={cn(inputClass, 'lowercase')}
                    value={discountForm.code}
                    onChange={(e) => setDiscountForm((c) => ({ ...c, code: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="discount-name" className="mb-1.5 block text-sm font-medium">
                    Name
                  </label>
                  <input
                    id="discount-name"
                    className={inputClass}
                    value={discountForm.name}
                    onChange={(e) => setDiscountForm((c) => ({ ...c, name: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="discount-permission" className="mb-1.5 block text-sm font-medium">
                  Required permission
                </label>
                <select
                  id="discount-permission"
                  className={inputClass}
                  value={discountForm.required_permission}
                  onChange={(e) =>
                    setDiscountForm((c) => ({ ...c, required_permission: e.target.value }))
                  }
                >
                  {DISCOUNT_PERMISSION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="discount-max" className="mb-1.5 block text-sm font-medium">
                    Max discount (%)
                  </label>
                  <input
                    id="discount-max"
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    className={inputClass}
                    value={discountForm.max_percent}
                    onChange={(e) => setDiscountForm((c) => ({ ...c, max_percent: e.target.value }))}
                    placeholder="No cap"
                  />
                </div>
                <div>
                  <label htmlFor="discount-sort" className="mb-1.5 block text-sm font-medium">
                    Sort order
                  </label>
                  <input
                    id="discount-sort"
                    type="number"
                    min={0}
                    className={inputClass}
                    value={discountForm.sort}
                    onChange={(e) => setDiscountForm((c) => ({ ...c, sort: e.target.value }))}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={discountForm.is_active}
                  onChange={(e) => setDiscountForm((c) => ({ ...c, is_active: e.target.checked }))}
                />
                Discount reason is active
              </label>
            </>
          ) : null}

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          {success ? (
            <p className="text-sm text-primary" role="status">
              {success}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : creating ? 'Create entry' : 'Save entry'}
            </Button>
          </div>
        </form>
      </MasterFormModal>
    </div>
  )
}
